/**
 * Where a trip lives, and what it is called. Nothing else.
 *
 * A separate file so that `trips.js` (one trip) and `trip-index.js` (the
 * list) can both use these without importing each other. A cycle between
 * those two would work today and break the first time a statement moved.
 *
 * Tenancy, per ARCHITECTURE.md §7: everything is under `users/{uid}/`.
 */

/**
 * The trips collection. Same path `/app` uses (app.html:8088), because both
 * apps must see the same trips until the legacy editor is retired.
 * @param {string} uid @returns {string[]}
 */
export function tripsPath(uid) {
  if (!uid) throw new Error('a uid is required')
  return ['users', uid, 'trips']
}

/**
 * One trip document. Keyed by `trip.id`, matching `/app` (app.html:8194).
 * @param {string} uid @param {string|number} id @returns {string[]}
 */
export function tripPath(uid, id) {
  const key = String(id == null ? '' : id).trim()
  if (!key) throw new Error('a trip id is required')
  return [...tripsPath(uid), key]
}

/**
 * The summary index: one document holding every trip's cheap view, so that
 * drawing a list of N trips costs one read instead of N.
 * @param {string} uid @returns {string[]}
 */
export function tripIndexPath(uid) {
  if (!uid) throw new Error('a uid is required')
  return ['users', uid, 'meta', 'tripIndex']
}

/**
 * A trip id that cannot collide.
 *
 * Legacy uses `'trip_' + Date.now()` (app.html:6242, and the live override at
 * 8410). Two trips created inside the same millisecond — which bulk import
 * does by definition — get the same id, and the second overwrites the first
 * because the id is the document key. The importer patched around it with its
 * own suffix (app.html:15347); this is that fix, in one place, for everyone.
 *
 * @param {number} [nowMs]
 * @param {number} [seq] - position within a batch
 * @returns {string}
 */
export function newTripId(nowMs = Date.now(), seq = 0) {
  const stamp = Number.isFinite(nowMs) ? Math.floor(Number(nowMs)) : 0
  const slot = Number.isFinite(seq) ? Math.max(0, Math.floor(Number(seq))) : 0
  const tail = Math.random().toString(36).slice(2, 8)
  return `trip_${stamp}_${slot}${tail}`
}
